<!-- The registration fees as #register's first step shows them (fullstack Register/Rates.vue):
     one row per registrant type, high-income country rates (*), Early-Bird prices until it ends (†),
     "Both" with the combined offer (‡), then the video hours each registration unlocks. A page
     has no event chips, so the events are columns; "Both" includes the combined offer
     (fullstack useOnlineEvents.js getCombinedOffer: $50 at the high-income rate).
     Replaces the calculator on the 2026 Registration page. -->

<template>
  <div class="regionalRates">
    <table>
      <thead>
        <tr>
          <th class="typeHeader" scope="col">Registrant Type<sup class="mark">*</sup></th>
          <th
            v-for="(column, index) in COLUMNS"
            :key="column"
            scope="col"
            :class="{isBoth: index === BOTH_INDEX}"
          >
            {{ column }}<sup v-if="index === BOTH_INDEX" class="mark">‡</sup>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in rows" :key="row.type">
          <th scope="row">{{ getBreakable(row.type) }}</th>
          <td
            v-for="(price, index) in row.prices"
            :key="index"
            :class="{isBoth: index === BOTH_INDEX}"
          >
            {{ currency }}{{ price }}<sup class="mark">†</sup>
          </td>
        </tr>
      </tbody>
    </table>
    <p class="footnote">
      <span class="mark">*</span>
      <span>High-income country rates<RatesTierHelp /></span>
    </p>
    <p class="footnote">
      <span class="mark">†</span>
      <span v-if="isEarlyBird">
        Early-Bird rates until {{ endText }}. Before ISCB &amp; DVS discounts. Free if you attended
        these events in April.
      </span>
      <span v-else>
        Before ISCB &amp; DVS discounts. Free if you attended these events in April.
      </span>
    </p>
    <p class="footnote">
      <span class="mark">‡</span>
      <span>
        Both events together include the {{ currency }}{{ COMBINED_OFFER }} combined offer.
      </span>
    </p>
    <!-- a statement, not a selection: neutral, not the dialog's green (Rates.vue .access) -->
    <!-- one flex item per phrase: lines break between phrases, never inside one; always after
         "Masterclass videos," (.lineBreak) -->
    <router-link v-if="counts" to="/Videos/Unlock" class="unlock" data-hint="Click to learn more">
      <span>Registration unlocks</span>
      <span
        ><b>{{ counts.masterclass.videos }} Masterclass videos</b>
        ({{ counts.masterclass.hours }}),</span
      >
      <span class="lineBreak" aria-hidden="true" />
      <span
        ><b>{{ counts.workshop.videos }} Workshop videos</b> ({{ counts.workshop.hours }}),</span
      >
      <span>or <b>{{ counts.both.videos }}</b> ({{ counts.both.hours }}) for both.</span>
    </router-link>
  </div>
</template>

<script setup>
import {computed} from "vue";
import RatesTierHelp from "./RatesTierHelp.vue";
import useLibraryTotals from "@/composables/useLibraryTotals";
import useEarlyBird, {getEarlyBirdPrice} from "@/composables/useEarlyBird";

const props = defineProps({
  events: {type: Object, required: true}, // events-online.json: {event: {Online: {Full Fee: …}}}
  currency: {type: String, default: "$"}
});

const COLUMNS = ["Masterclass", "Workshop", "Both"];
const BOTH_INDEX = COLUMNS.indexOf("Both");
const COMBINED_OFFER = 50;

// "Academic/Freelancer" may break after the slash (zero-width space): on phones the table must
// fit the page column, which otherwise widens to it
function getBreakable(label) {
  return label.replaceAll("/", "/\u200b");
}

function getFees(eventName) {
  return props.events[eventName]?.Online?.["Full Fee"] || {};
}

// until the Early-Bird ends, each event at its Early-Bird price, as Pay charges it; "Both" is the
// two less the combined offer
const {isEarlyBird, endText} = useEarlyBird();
const getPrice = (fee) => (isEarlyBird.value ? getEarlyBirdPrice(fee) : fee);

function getRow(type, masterclassFees, workshopFees) {
  const [masterclass, workshop] = [getPrice(masterclassFees[type]), getPrice(workshopFees[type])];
  return {type, prices: [masterclass, workshop, masterclass + workshop - COMBINED_OFFER]};
}

// what each registration unlocks: "21 Masterclass videos (28 hours)", "85 (66 hours) for both"
// (the Masterclass language guide's form, without repeating "videos"),
// from the same stats.json as #register's "Unlocks … hours"
const {stats} = useLibraryTotals();
const getCount = (entry) => ({videos: entry?.videos, hours: `${entry?.hours} hours`});
const counts = computed(() => {
  if (!stats.value) return null;
  const {masterclass, workshop, both} = stats.value;
  return {masterclass: getCount(masterclass), workshop: getCount(workshop), both: getCount(both)};
});

const rows = computed(() => {
  const masterclassFees = getFees("VIZBI Masterclass");
  const workshopFees = getFees("VIZBI Workshop");
  return Object.keys(masterclassFees).map((type) => getRow(type, masterclassFees, workshopFees));
});
</script>

<style scoped>
.regionalRates {
  --row-stripe: color-mix(in srgb, var(--text-color) 5%, var(--background-color)); /* ▶ EDIT */
  margin: 1rem 0 1.5rem;
}
table {
  width: 100%;
  border-collapse: collapse;
  font-variant-numeric: tabular-nums;
}
th,
td {
  padding: 0.45rem 0.5rem;
}
/* rows striped as in Finder: odd rows just off the page background, even rows the page itself */
/* the header counts as row one: it and even body rows take the stripe; no rule under it */
thead tr,
tbody tr:nth-child(even) {
  background: var(--row-stripe);
}
thead th {
  text-transform: uppercase; /* reads as a header without a border */
  letter-spacing: 0.03em;
  font-size: 0.85rem;
  font-weight: 600;
  text-align: right;
  color: var(--text-color-less-subtle);
}
thead th.typeHeader {
  text-align: left;
}
tbody th {
  text-align: left;
  font-weight: 500;
}
td {
  text-align: right;
  font-weight: 600;
  white-space: nowrap;
}
/* "Both" and its prices: the heaviest weight loaded (Nunito Sans 800, public/index.html) */
.isBoth,
thead th.isBoth {
  font-weight: 800;
}
.mark {
  font-size: 0.75em;
  margin-left: 0.1em;
}
.footnote {
  display: flex;
  gap: 0.35rem;
  margin: 0.35rem 0 0;
  font-size: 0.85rem;
  line-height: 1.35;
  color: var(--text-color-less-subtle);
}
.footnote .mark {
  font-size: 1em;
  margin-left: 0;
}
/* like the value bar on #register's first step: light fill, clear border, rounded rectangle */
.unlock {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0 0.3em; /* ▶ EDIT HERE: the space between phrases */
  position: relative; /* anchors the hover hint */
  margin: 1rem 0 0; /* full width of the table above */
  padding: 0.275rem 0.75rem; /* ▶ EDIT HERE: close to the edge, reads as a button */
  border: 1px solid var(--border-color);
  border-radius: 17px; /* same as Register Now in the banner (CallToAction/RegisterNow.vue) */
  /* inverted: muted white with dark text in either mode, so it stands apart from a dark page */
  background: #dcdcd9; /* ▶ EDIT HERE: muted white */
  color: #202124;
  text-align: center;
  font-weight: 500;
  line-height: 1.2; /* ▶ EDIT HERE: the gap between the lines (0.2em), in step with the padding */
}
.unlock {
  text-decoration: none; /* the whole box is the link to /Videos/Unlock */
}
.unlock:hover {
  border-color: var(--text-color-less-subtle);
}
/* "Click to learn more": shown just below the block on hover, never inside it */
.unlock::after {
  content: attr(data-hint);
  position: absolute;
  top: calc(100% + 0.3rem);
  left: 50%;
  transform: translateX(-50%);
  padding: 0.15rem 0.5rem;
  border-radius: 0.3rem;
  background: var(--text-color);
  color: var(--background-color);
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s;
}
.unlock:hover::after {
  opacity: 1;
}
.unlock span {
  white-space: nowrap;
}
.unlock .lineBreak {
  flex-basis: 100%;
  height: 0;
}
.unlock b {
  font-weight: 800;
}
/* the smallest phones (320px): slightly smaller text keeps the table inside the page column */
/* phones (375–430px, e.g. iPhones): a tighter header and cells, or the table is wider than the
   page column (391px at 375–414px) */
@media (max-width: 440px) {
  thead th {
    font-size: 0.72rem; /* ▶ EDIT HERE */
    letter-spacing: 0;
  }
  th,
  td {
    padding-inline: 0.3rem;
  }
}
@media (max-width: 360px) {
  table {
    font-size: 0.9rem;
  }
  thead th {
    font-size: 0.64rem; /* uppercase is wider: "MASTERCLASS" must still fit */
    letter-spacing: 0;
  }
  th,
  td {
    padding-inline: 0.25rem;
  }
}
</style>
